'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { trackError } from '@sola/cookies/components/datalayer.js';
import { AnyObject } from '@sola/schema/dotcom/helpers.js';
import { getListSearchResultsQuery } from '@sola/skippy-search-sdk/lib/queries.js';
import { QueryResponsePublished } from '@sola/skippy-search-sdk/lib/schema.js';

export const parseUrlParams = ({
  searchParams,
  schema,
}: {
  searchParams: URLSearchParams;
  schema: z.ZodSchema;
}) => {
  const plainSearchParams = Object.fromEntries(searchParams.entries());
  try {
    const validatedParams = schema.parse(plainSearchParams);
    return new URLSearchParams(validatedParams as AnyObject);
  } catch (error) {
    if (error instanceof z.ZodError) {
      trackError(error.message);
      console.error('Validation error:', error.issues);
    } else {
      trackError(error as string);
      console.error('Unexpected error:', error);
    }
  }
};

export const getLegacyQuery = () => {
  if (typeof window !== 'undefined' && window.location.hash) {
    return window.location.hash
      .replace(/^#/, '')
      .split('&')
      .find((segment) => segment.startsWith('stq='))
      ?.replace('stq=', '');
  }
};

const getParsedParams = (params: URLSearchParams, schema: z.ZodSchema) => {
  return parseUrlParams({
    searchParams: new URLSearchParams(params.toString()),
    schema,
  });
};

export const useUrlState = (schema: z.ZodSchema) => {
  const pathname = usePathname();
  const searchParams = getParsedParams(useSearchParams(), schema);

  const filters = {
    searchQuery: searchParams?.get('q') || '',
    currentPage: Number(searchParams?.get('page')) || 0,
  };

  const updateSearchParams = useCallback(
    (updatedParams: Record<string, string | undefined>) => {
      Object.entries(updatedParams).forEach(([key, value]) => {
        if (value) {
          searchParams?.set(key, value);
        } else {
          searchParams?.delete(key);
        }
      });

      const queryString = searchParams?.toString();
      window.history.replaceState(
        null,
        '',
        queryString ? `${pathname}?${queryString}` : pathname,
      );
    },
    [pathname, searchParams?.toString()],
  );

  return {
    searchParams,
    filters,
    updateSearchParams,
  };
};

export const useSearchAPI = (
  searchQuery: string,
  itemOffset: number,
  itemsPerPage: number,
  searchParams?: URLSearchParams,
) => {
  const [searchResultsList, setSearchResultsList] =
    useState<QueryResponsePublished>();

  useEffect(() => {
    const getAllResults = async () => {
      const results = await getListSearchResultsQuery({
        perPage: itemsPerPage,
        offset: Number(searchParams?.get('page')) * itemsPerPage || itemOffset,
        query: searchQuery,
      });
      setSearchResultsList(results);
    };

    getAllResults();
  }, [
    searchQuery,
    itemOffset,
    itemsPerPage,
    searchParams?.get('page'),
    searchParams?.get('q'),
  ]);

  return searchResultsList;
};
